import React, { useEffect, useRef, useState } from 'react';
import { storyblokEditable } from 'gatsby-source-storyblok';
import S from './Input.styled';
import { FormLabel } from '@/styles/typography';
import { Flex } from '@/styles/global';
import { FieldError } from 'react-hook-form';
import useCountriesData from '@/src/data/useCountriesData';

export interface IInputBlok {
  label: string;
  key: string;
  placeholder: string;
  type:
    | 'text'
    | 'email'
    | 'number'
    | 'tel'
    | 'textarea'
    | 'country'
    | 'dropdown';
  required?: boolean;
  dropdown_options?: { option: string }[];
  fullWidth?: boolean;
}

export interface IInputProps {
  blok: IInputBlok;
  textColor?: string;
  value?: string;
  onInputChange?: (key: string, value: string) => void;
  register?: any;
  error?: FieldError | undefined;
  width?: string | number;
  setValue?: (name: string, value: any, options?: object) => void;
}

const Input: React.FC<IInputProps> = ({
  blok,
  blok: { label, key, placeholder, type, required, dropdown_options },
  textColor,
  onInputChange = () => {},
  register,
  error,
  width,
  setValue,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState('');
  const [selectedDropdown, setSelectedDropdown] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const countries = useCountriesData();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filtered countries based on search term
  const filteredCountries = countries.filter((country) =>
    country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onInputChange(key, e.target.value);
  };

  const handleSelect = (country: string) => {
    setSelected(country);
    setIsOpen(false);
    setSearchTerm(''); // Reset search term when a country is selected
    onInputChange(key, country);

    if (setValue) {
      setValue(key, country, { shouldValidate: true });
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelected('');
    setSearchTerm(e.target.value);
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Flex $column {...storyblokEditable(blok)} $width={'100%'}>
      {label && (
        <FormLabel $color={textColor} htmlFor={key}>
          {label}
          {required && '*'}
        </FormLabel>
      )}
      {type === 'textarea' ? (
        <S.TextArea
          onChange={handleChange}
          placeholder={placeholder}
          $error={!!error}
          id={key}
        />
      ) : type === 'country' ? (
        <S.DropdownWrapper $width={width} ref={dropdownRef}>
          <S.Search
            type="text"
            onClick={() => setIsOpen(!isOpen)}
            $error={error}
            aria-expanded={isOpen}
            value={selected ? selected : searchTerm || ''}
            aria-haspopup="listbox"
            onChange={handleSearch}
            placeholder={placeholder || ''}
          />
          {isOpen && (
            <S.DropdownContent>
              <S.DropdownList role="listbox" open={isOpen} $error={error}>
                {filteredCountries.map((country) => (
                  <S.DropdownItem
                    key={country}
                    role="option"
                    onClick={() => handleSelect(country)}
                  >
                    {country}
                  </S.DropdownItem>
                ))}
                {filteredCountries.length === 0 && (
                  <S.NoResults>No results found</S.NoResults>
                )}
              </S.DropdownList>
            </S.DropdownContent>
          )}
        </S.DropdownWrapper>
      ) : type === 'dropdown' ? (
        <S.DropdownWrapper $width={width} ref={dropdownRef}>
          <S.SelectedItem
            onClick={() => setIsOpen(!isOpen)}
            $error={error}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
          >
            {selectedDropdown || placeholder || ''}
          </S.SelectedItem>
          {isOpen && (
            <S.DropdownContent>
              <S.DropdownList role="listbox" open={isOpen} $error={error}>
                {dropdown_options?.map((option, index) => {
                  return (
                    <S.DropdownItem
                      key={index}
                      role="option"
                      onClick={() => {
                        setSelectedDropdown(option.option);
                        setIsOpen(false);
                        onInputChange(key, option.option);
                      }}
                    >
                      {option.option}
                    </S.DropdownItem>
                  );
                })}
              </S.DropdownList>
            </S.DropdownContent>
          )}
        </S.DropdownWrapper>
      ) : (
        <S.Input
          type={type}
          {...register}
          onChange={handleChange}
          placeholder={placeholder}
          $error={error}
          // $width={width}
          id={key}
        />
      )}
    </Flex>
  );
};

export default Input;
